import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const DefectConfigContext = createContext({
  disabledLabels: [],
  loading: false,
  fetchConfig: () => Promise.resolve(),
  saveConfig: () => Promise.resolve(),
  isLabelEnabled: () => true,
});

export const useDefectConfig = () => useContext(DefectConfigContext);

export const DefectConfigProvider = ({ children }) => {
  const [disabledLabels, setDisabledLabels] = useState([]);
  const [loading, setLoading] = useState(false);

  const getToken = () => {
    try {
      const raw = localStorage.getItem('token');
      return raw ? raw.replace(/^"|"$/g, '') : null;
    } catch { return null; }
  };

  const fetchConfig = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}defectConfig/get`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDisabledLabels(res.data?.result?.disabled_labels || []);
    } catch (err) {
      console.warn('Failed to fetch defect config:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveConfig = useCallback(async (newDisabledLabels) => {
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}defectConfig/update`,
        { disabled_labels: newDisabledLabels },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setDisabledLabels(newDisabledLabels);
    } catch (err) {
      console.error('Failed to save defect config:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const isLabelEnabled = useCallback((label) => {
    if (!disabledLabels || disabledLabels.length === 0) return true;
    const norm = label.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    return !disabledLabels.some(d => {
      const dNorm = d.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
      return dNorm === norm || norm.includes(dNorm) || dNorm.includes(norm);
    });
  }, [disabledLabels]);

  // Fetch on mount (when user is logged in)
  useEffect(() => {
    const token = getToken();
    if (token) fetchConfig();
  }, [fetchConfig]);

  return (
    <DefectConfigContext.Provider value={{ disabledLabels, loading, fetchConfig, saveConfig, isLabelEnabled }}>
      {children}
    </DefectConfigContext.Provider>
  );
};

export default DefectConfigContext;
